import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import BusinessIcon from "@mui/icons-material/Business";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import PageHeader from "../../../app/layout/PageHeader";
import { api } from "../../../api/apiClient";
import { useStaticData } from "../../../context/StaticDataContext";
import { notify } from "../../components-ui/toast";
import "./tenant-administration.css";

interface OrganisationSummary {
  tenantId: number;
  name: string;
  slug: string;
  description?: string;
  isActive: boolean;
}

export default function SystemOrganisationManagementPage() {
  const history = useHistory();
  const { data } = useStaticData();
  const [organisations, setOrganisations] = useState<OrganisationSummary[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [organisationTypeId, setOrganisationTypeId] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api<OrganisationSummary[]>("/api/tenant-administration")
      .then(setOrganisations)
      .catch((error) => {
        console.error("Failed to load organisations", error);
        notify.error("Failed to load organisations");
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredOrganisations = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return organisations;

    return organisations.filter((organisation) =>
      [organisation.name, organisation.description, organisation.slug].some(
        (value) => value?.toLowerCase().includes(term),
      ),
    );
  }, [organisations, search]);

  const openOrganisation = (tenantId: number) => {
    history.push(`/admin/manage-tenants?tenantId=${tenantId}`);
  };

  const createOrganisation = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !organisationTypeId) {
      notify.error("Enter an organisation name and select an organisation type");
      return;
    }

    try {
      setCreating(true);
      const created = await api<OrganisationSummary>(
        "/api/tenant-administration",
        {
          method: "POST",
          body: {
            name: name.trim(),
            description: description.trim() || null,
            organisationTypeId: Number(organisationTypeId),
          },
        },
      );
      notify.success("Organisation created successfully");
      openOrganisation(created.tenantId);
    } catch (error) {
      console.error("Failed to create organisation", error);
      notify.error("Failed to create organisation");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="tenant-admin-page">
      <PageHeader
        title="Organisation Management"
        subtitle="Select an organisation to manage its details and profile."
        actions={
          <button
            type="button"
            className="icon-button-solid icon-button-solid--success"
            onClick={() => setShowCreate((value) => !value)}
            title={showCreate ? "Close new organisation form" : "Create organisation"}
            aria-label={showCreate ? "Close new organisation form" : "Create organisation"}
            aria-expanded={showCreate}
          >
            {showCreate ? <CloseIcon fontSize="small" /> : <AddIcon fontSize="small" />}
          </button>
        }
      />

      {showCreate && (
        <form className="card tenant-admin-create" onSubmit={createOrganisation}>
          <div className="card-header">
            <div className="card-title">New organisation</div>
          </div>
          <p className="tenant-admin-hint">
            Create an organisation, then complete its details and profile.
          </p>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="organisation-name">Name</label>
              <input
                id="organisation-name"
                className="form-input"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Enter the organisation name"
                maxLength={200}
                autoFocus
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="organisation-type">Type</label>
              <select
                id="organisation-type"
                className="form-input"
                value={organisationTypeId}
                onChange={(event) => setOrganisationTypeId(event.target.value)}
                required
              >
                <option value="">Select</option>
                {(data?.organisationTypes ?? []).map((type) => (
                  <option key={type.organisationTypeId} value={type.organisationTypeId}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="organisation-description">
              Description
            </label>
            <textarea
              id="organisation-description"
              className="form-input"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={500}
              rows={3}
            />
          </div>
          <div className="tenant-admin-create-actions">
            <button className="form-submit-button" type="submit" disabled={creating}>
              {creating ? "Creating…" : "Create organisation"}
            </button>
            <button
              className="form-submit-button form-submit-button--secondary"
              type="button"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="card tenant-admin-empty">Loading organisations...</div>
      ) : (
        <div className="organisation-management-list">
          <div className="organisation-management-toolbar">
            <div className="search-container organisation-management-search">
              <div className="search-input-wrapper">
                <SearchIcon className="search-icon-left" fontSize="small" />
                <input
                  className="form-input search-input-with-icons"
                  placeholder="Search organisations..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  aria-label="Search organisations"
                />
                {search && (
                  <button
                    type="button"
                    className="search-clear-button"
                    onClick={() => setSearch("")}
                    aria-label="Clear organisation search"
                  >
                    <CloseIcon fontSize="small" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {filteredOrganisations.length === 0 ? (
            <div className="card tenant-admin-empty">
              {organisations.length === 0
                ? "No organisations are available to manage."
                : "No organisations found."}
            </div>
          ) : (
            <div className="organisation-card-grid">
              {filteredOrganisations.map((organisation) => (
                <button
                  type="button"
                  key={organisation.tenantId}
                  className="organisation-summary-card"
                  onClick={() => openOrganisation(organisation.tenantId)}
                  aria-label={`Open ${organisation.name}`}
                >
                  <span className="organisation-summary-main">
                    <span className="organisation-summary-icon">
                      <BusinessIcon />
                    </span>
                    <span className="organisation-summary-copy">
                      <strong>{organisation.name}</strong>
                      <span>{organisation.description || "Organisation"}</span>
                      <span className="organisation-summary-meta">
                        <span
                          className={`organisation-status-dot${
                            organisation.isActive ? "" : " is-inactive"
                          }`}
                        />
                        {organisation.isActive ? "Active" : "Inactive"}
                      </span>
                    </span>
                  </span>
                  <span className="organisation-summary-rail" aria-hidden="true">
                    <ChevronRightIcon fontSize="small" />
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
